import { PrototypeApiError } from '../apiClient';
import { byId } from '../domUtils';
import { closeModal, openModal } from '../navigationController';
import type { PrototypeRuntime, Root } from '../prototypeTypes';
import {
	AUTH_RESEND_VERIFICATION_TURNSTILE_ACTION,
	lockSubmitButton,
} from '../turnstileWidgetController';
import { disposeModalTurnstile, reconcileModalTurnstile, restartModalTurnstile } from '../modalTurnstileLifecycle';
import { setAuthButtonLoading } from './authButtonLock';
import { feedbackResult, feedbackResultIcon, type FeedbackResultKind } from '../feedbackResult';

const pendingEmailByView = new WeakMap<HTMLElement, string>();
const inFlightViews = new WeakSet<HTMLElement>();
const passwordProvenRecoveryViews = new WeakSet<HTMLElement>();

/**
 * Terminal recovery statuses where the one-time handle is permanently
 * exhausted. The user must sign in again to obtain a fresh recovery
 * handle -- re-rendering the Turnstile and re-enabling Resend would
 * create a guaranteed-failure dead loop (server returns 409
 * recovery_handle_consumed).
 */
const TERMINAL_RECOVERY_STATUSES = new Set(['queue_unavailable', 'consumed', 'provider_bounced']);

function isTerminalRecoveryStatus(status: string): boolean {
	return TERMINAL_RECOVERY_STATUSES.has(status);
}

/**
 * Opens the single pending-verification surface used by both registration
 * and an email/password login rejected as unverified. The email remains in
 * memory only; it is never painted into the DOM or written to diagnostics.
 */
export async function showEmailVerificationPendingContext(
	root: Root,
	runtime: PrototypeRuntime,
	email: string,
	mode: 'verification_sent' | 'existing_account' | 'login_rejected' = 'verification_sent',
	verificationPending = mode === 'verification_sent',
): Promise<void> {
	const pending = byId(root, 'registerSuccess');
	const form = byId(root, 'registerForm');
	const normalizedInput = email.trim();
	if (!pending || !normalizedInput) return;

	pendingEmailByView.set(pending, normalizedInput);
	if (mode === 'login_rejected') passwordProvenRecoveryViews.add(pending);
	else passwordProvenRecoveryViews.delete(pending);
	paintRegistrationResult(root, mode === 'verification_sent' ? 'success' : 'info', {
		title: mode === 'existing_account' ? 'Account already exists' : mode === 'login_rejected' ? 'Email verification required' : 'Check your email',
		detail: mode === 'existing_account'
		? verificationPending
			? 'This account still needs email verification. Use the original password after verifying, or continue with Google.'
			: 'Continue with Google or log in with your password. If you do not remember it, reset it.'
		: mode === 'login_rejected'
			? 'This account needs email verification. Request a new verification link to continue.'
		: "We've sent a verification link to your email. Click the link to activate your account.",
	});
	// Fresh signup shows only "Log in" -- no resend on the check-your-email
	// screen. Resend stays on the recovery surfaces (existing_account with a
	// pending verification, and login_rejected) where it is the recovery path.
	paintRecoveryActions(root, mode !== 'login_rejected', mode === 'verification_sent' ? false : verificationPending);
	if (form) form.style.display = 'none';
	pending.style.display = 'block';
	paintFeedback(root, null);
	closeModal(root, 'loginModal');
	if (!byId(root, 'registerModal')?.classList.contains('active')) openModal(root, 'registerModal');
	await reconcileModalTurnstile(root, 'registerModal', runtime);
}

/** Restores the normal registration form when the user starts a new signup. */
export function resetEmailVerificationPendingContext(root: Root): void {
	const pending = byId(root, 'registerSuccess');
	const form = byId(root, 'registerForm');
	if (pending) {
		pendingEmailByView.delete(pending);
		passwordProvenRecoveryViews.delete(pending);
		pending.style.display = 'none';
	}
	if (form) form.style.display = 'block';
	paintRegistrationResult(root, 'success', {
		title: 'Check your email',
		detail: "We've sent a verification link to your email. Click the link to activate your account.",
	});
	paintRecoveryActions(root, true, false);
	paintFeedback(root, null);
}

/**
 * Handles the one-shot resend action. It deliberately returns normally for
 * an inactive or missing context so a forged delegated action cannot turn
 * this into an account-enumeration endpoint.
 */
export async function handleEmailVerificationResend(root: Root, runtime: PrototypeRuntime): Promise<void> {
	const pending = byId(root, 'registerSuccess');
	const modal = byId(root, 'registerModal');
	if (!pending || pending.style.display === 'none' || !modal?.classList.contains('active')) return;
	const email = pendingEmailByView.get(pending);
	if (!email || inFlightViews.has(pending)) return;

	const passwordProvenRecovery = passwordProvenRecoveryViews.has(pending);
	const provider = runtime.interactiveTurnstile;
	if (!provider?.hasToken()) {
		paintFeedback(root, 'Please complete the security verification before requesting a new link.');
		return;
	}

	const button = byId(root, 'resendVerificationSubmitBtn') as HTMLButtonElement | null;
	const originalText = button?.textContent ?? undefined;
	inFlightViews.add(pending);
	setAuthButtonLoading(button, true, originalText, 'Sending…');
	let completed = false;
	try {
		const turnstileToken = await provider.getToken(AUTH_RESEND_VERIFICATION_TURNSTILE_ACTION);
		if (passwordProvenRecovery) {
			const recovery = await runtime.api.requestEmailVerificationRecovery(turnstileToken);
			const latest = await runtime.api.emailVerificationRecoveryStatus().catch(() => recovery);
			if (isCurrentPendingContext(root, pending, email)) {
				if (isDeliveryAccepted(latest.status)) {
					showVerificationEmailSent(root, runtime);
					completed = true;
				} else if (isTerminalRecoveryStatus(latest.status)) {
					showTerminalRecoveryFailure(root, runtime, latest.status);
					completed = true;
				} else {
					paintFeedback(root, describeRecoveryStatus(latest.status));
				}
			}
		} else {
			await runtime.api.resendEmailVerification({ email, turnstileToken });
			if (isCurrentPendingContext(root, pending, email)) {
				showVerificationEmailSent(root, runtime);
				completed = true;
			}
		}
	} catch (error) {
		runtime.lastApiError = error instanceof Error ? error.message : String(error);
		if (passwordProvenRecovery) {
			const reconciled = await runtime.api.emailVerificationRecoveryStatus().catch(() => null);
			if (reconciled && reconciled.status !== 'issued') {
				if (isCurrentPendingContext(root, pending, email)) {
					if (isDeliveryAccepted(reconciled.status)) {
						showVerificationEmailSent(root, runtime);
						completed = true;
					} else if (isTerminalRecoveryStatus(reconciled.status)) {
						showTerminalRecoveryFailure(root, runtime, reconciled.status);
						completed = true;
					} else {
						paintFeedback(root, describeRecoveryStatus(reconciled.status));
					}
				}
				return;
			}
		}
		const status = error instanceof PrototypeApiError ? error.status : 0;
		if (isCurrentPendingContext(root, pending, email)) paintFeedback(root, describeResendFailure(status));
	} finally {
		inFlightViews.delete(pending);
		if (!completed && isCurrentPendingContext(root, pending, email)) {
			lockSubmitButton(root, 'resendVerificationSubmitBtn');
			setAuthButtonLoading(button, false, originalText);
			await restartModalTurnstile(root, 'registerModal', runtime);
		} else {
			setAuthButtonLoading(button, false, originalText);
		}
	}
}

function isDeliveryAccepted(status: 'issued' | 'consumed' | 'enqueued' | 'provider_accepted' | 'delivered' | 'provider_bounced' | 'queue_unavailable'): boolean {
	return status === 'enqueued' || status === 'provider_accepted' || status === 'delivered';
}

function showVerificationEmailSent(root: Root, runtime: PrototypeRuntime): void {
	const pending = byId(root, 'registerSuccess');
	if (pending) {
		pendingEmailByView.delete(pending);
		passwordProvenRecoveryViews.delete(pending);
	}
	paintRegistrationResult(root, 'success', {
		title: 'Verification email sent',
		detail: 'Check your inbox and open the verification link to continue.',
	});
	paintRecoveryActions(root, false, false);
	paintFeedback(root, null);
	disposeModalTurnstile(root, 'registerModal', runtime);
}

/**
 * Terminal failure: the one-time recovery handle is permanently exhausted
 * (queue_unavailable, consumed) or the email bounced. The user must sign in
 * again to get a fresh handle. Hiding the Resend control and Turnstile
 * prevents a guaranteed-failure dead loop (server returns 409
 * recovery_handle_consumed on the next attempt).
 */
function showTerminalRecoveryFailure(root: Root, runtime: PrototypeRuntime, status: string): void {
	const pending = byId(root, 'registerSuccess');
	if (pending) {
		pendingEmailByView.delete(pending);
		passwordProvenRecoveryViews.delete(pending);
	}
	const isBounced = status === 'provider_bounced';
	paintRegistrationResult(root, 'info', {
		title: isBounced ? 'Email could not be delivered' : 'Verification request stopped',
		detail: describeRecoveryStatus(status as never),
	});
	// Show only "Back to Log in"; hide Resend + Turnstile permanently.
	paintRecoveryActions(root, true, false);
	paintFeedback(root, null);
	disposeModalTurnstile(root, 'registerModal', runtime);
}

function paintRecoveryActions(root: Root, showLogin: boolean, showResend: boolean): void {
	const login = byId(root, 'registerRecoveryLoginBtn');
	const resend = byId(root, 'resendVerificationControls');
	if (login) login.hidden = !showLogin;
	if (resend) resend.hidden = !showResend;
}

function describeRecoveryStatus(status: 'issued' | 'consumed' | 'enqueued' | 'provider_accepted' | 'delivered' | 'provider_bounced' | 'queue_unavailable'): string {
	if (status === 'queue_unavailable') return 'We could not queue this verification email. Please sign in again with your email and password to request a new link.';
	if (status === 'enqueued') return 'Your verification email is queued for delivery.';
	if (status === 'provider_accepted') return 'Your email provider accepted the verification message.';
	if (status === 'delivered') return 'Your verification email was delivered. Check your inbox for the link.';
	if (status === 'provider_bounced') return 'We could not deliver this email. Correct the address or contact support.';
	if (status === 'consumed') return 'This verification request stopped before email handoff. Sign in again to request a new link.';
	return 'Your verification recovery is ready. Send one new link when you are ready.';
}

function isCurrentPendingContext(root: Root, pending: HTMLElement, email: string): boolean {
	return pending.style.display !== 'none'
		&& byId(root, 'registerModal')?.classList.contains('active') === true
		&& pendingEmailByView.get(pending) === email;
}

function describeResendFailure(status: number): string {
	if (status === 403) return 'Security verification could not be confirmed. Complete the new check and try again.';
	if (status === 429) return 'Too many requests. Please wait before trying again.';
	if (status === 503) return 'Verification email service is temporarily unavailable. Please try again shortly.';
	if (status === 0) return 'Could not reach the verification service. Check your connection and try again.';
	return 'Could not request a new verification link. Please try again.';
}

function paintFeedback(root: Root, message: string | null): void {
	const feedback = byId(root, 'resendVerificationFeedback');
	if (!feedback) return;
	feedback.textContent = message ?? '';
	feedback.hidden = !message;
}

function paintRegistrationResult(root: Root, kind: FeedbackResultKind, copy: { title: string; detail: string }): void {
	const result = feedbackResult(kind, copy);
	const icon = byId(root, 'registerFeedbackIcon');
	if (icon) {
		icon.dataset.feedbackKind = result.kind;
		icon.setAttribute('aria-label', result.ariaLabel);
		icon.style.color = `var(${result.token})`;
		icon.style.background = `var(${result.token}-bg)`;
		icon.innerHTML = feedbackResultIcon(result.kind);
	}
	const title = byId(root, 'registerRecoveryTitle');
	const detail = byId(root, 'registerRecoveryDetail');
	if (title) title.textContent = result.title;
	if (detail) detail.textContent = result.detail;
}
